import { getCropCssColor } from "@omnia/shared/fields";
import { defineDirective, ICrop, IOmniaSession, IOmniaShell, OrderInvoice, range, RoleId, shell } from "../omnia"
import { ISoilAnalysisOrder, ISoilService } from "../types"
import { getPcnAnalysisFields, getSoilAnalysisFields } from "./fields"
import { IFieldSelectionViewModel, OrderStep } from "./types"

export class AnalysisOrderPopupCtrl extends shell.Dialog {

    static create() {

        return defineDirective(AnalysisOrderPopupCtrl, "app/soil/order/orderPopup.html", {});

    }

    public fieldSelection: IFieldSelectionViewModel[];
    public filteredSelection: IFieldSelectionViewModel[];

    private _vmProviders: {
        [key in ("std" | "pcn")]: {
            vm: IFieldSelectionViewModel[],
            provider: () => ng.IPromise<IFieldSelectionViewModel[]>
        }
    }

    public order: ISoilAnalysisOrder;
    public step: OrderStep;

    private _pcn: boolean = false

    public layerYearFilterOptions: { value: number, text: string }[]
    private _layerYear: number = 0

    public dateFilterOptions: { value: number, text: string }[]
    private _years: number = 3

    private _zones: boolean = false


    static $inject = ["$scope", "OmniaShell", "OmniaSession", "OmniaSoilService"];

    constructor($scope: ng.IScope, shell: IOmniaShell, private session: IOmniaSession, private svc: ISoilService) {
        super($scope, shell)
    }

    $onInit() {

        this.step = OrderStep.FieldSelection

        this._pcn = !!(this.arguments && this.arguments.pcn)

        this.order = {
            Brand: this.shell.config.appId,
            HoldingId: this.svc.holdingId,
            YearId: this.svc.yearId,
            UserId: this.session.currentUser.Id,
            Invoicing: OrderInvoice.Grower,
            SamplingCode: null,
            AdditionalAnalysis: null,
            InvoicingInstructions: null
        }

        this.dateFilterOptions = [
            { value: 0, text: 'No date limit' },
            { value: 1, text: '> 1 year' }
        ].concat(range(9).map(a => ({
            value: 2 + a,
            text: `> ${2 + a} years`
        })))

        const thisYear = new Date().getFullYear()

        this.layerYearFilterOptions = [
            { value: 0, text: 'Latest' }
        ].concat(range(10).map(a => ({
            value: thisYear - a,
            text: String(thisYear - a)
        })))

        this.setupViewModelProviders()
    }


    private setupViewModelProviders() {

        this._vmProviders = {
            std: {
                vm: <IFieldSelectionViewModel[]>undefined,
                provider: () => getSoilAnalysisFields(this.svc, this._layerYear)
            },
            pcn: {
                vm: <IFieldSelectionViewModel[]>undefined,
                provider: () => getPcnAnalysisFields(this.svc, this._layerYear)
            }
        }

        this.setupViewModels()

    }

    private setupViewModels() {

        const type = this._pcn ? this._vmProviders.pcn : this._vmProviders.std

        const provider = type.vm
            ? this.shell.resolve(type.vm)
            : type.provider().then(vm => type.vm = vm)

        provider.then(() => {
            type.vm.forEach(t => t.selected = false)
            this.fieldSelection = type.vm
            this.filter()
        })

    }

    get pcnAnalysis() {
        return this._pcn
    }

    set pcnAnalysis(value) {
        if (this._pcn != value) {
            this._pcn = value
            this.setupViewModels()
        }
    }

    get layerYear() {
        return this._layerYear
    }

    set layerYear(value) {
        this._layerYear = value
        this.setupViewModelProviders()
    }

    get years() {
        return this._years
    }

    set years(value) {
        this._years = value
        this.filter()
    }

    get zones() {
        return this._zones
    }

    set zones(value: boolean) {
        this._zones = value
        this.filter()
    }

    getActualSelection() {
        return (this.filteredSelection || []).filter(s => (s.selected) && (!!s.zoneId == !!this.zones))
    }

    get noResults(): boolean {
        return this.filteredSelection && !this.filteredSelection.length;
    }

    get selectedCount(): number {
        return this.getActualSelection().length
    }

    get agronomist(): boolean {
        return this.session.currentUserRole <= RoleId.Agronomist
    }

    getDateString(date: Date): string {
        return date ? date.getDate() + "/" + (date.getMonth() + 1) + "/" + date.getFullYear() : null;
    }

    getCropColor(crop: ICrop) {
        if (crop && crop.CropId) {
            return getCropCssColor(crop)
        }
    }

    selectZones(field: IFieldSelectionViewModel) {
        const zones = this.filteredSelection.filter(s => s.fieldId === field.fieldId)
        const select = !zones.every(z => z.selected)
        zones.forEach(z => z.selected = select)
    }

    selectAll(select: boolean = true) {
        const scope = this.fieldSelection.filter(f => this.zones == (!!f.zoneId))
        scope.forEach(s => s.selected = select)
    }

    unselectAll() {
        this.selectAll(false)
    }

    filter() {

        const scope = this.fieldSelection.filter(f => this.zones == (!!f.zoneId))

        const today = new Date()

        const maxDate = this._years
            ? new Date(today.getFullYear() - this._years, today.getMonth() + 1, 1)
            : null

        let results = scope.filter(f => this.filterItem(f, maxDate))

        if (this.zones) {
            const fields = this.fieldSelection.filter(f => (!f.zoneId) && results.some(z => z.fieldId == f.fieldId))
            results = results.concat(fields)
        }

        this.filteredSelection = results

        return results

    }

    filterItem(f: IFieldSelectionViewModel, maxDate: Date) {

        if (f.selected) return true


        if (maxDate && f.analysisDate) {
            return (maxDate.valueOf() >= f.analysisDate.valueOf())
        }

        return true

    }

    canContinue(): boolean {
        return this.selectedCount > 0
    }

    doContinue() {
        this.step = OrderStep.FormInput;
    }

    canCommit(): boolean {
        return !!this.order.SamplingCode;
    }

    commit() {

        shell.showOkCancel(this.shell,
            "Remember that you must use the correct type of labels for printing. Use waterproof labels with 21 per page, with each label being 63.5 x 38.1mm.<br/><br/>Go to the Reports module to download the labels.",
            "Warning",
            "Create paperwork").then(r => {
                if (r) {
                    this.svc.orderSoilAnalysis(this.order,
                        this.getActualSelection().map(f => {
                            return {
                                FieldId: f.fieldId,
                                ZoneId: f.zoneId,
                                FieldName: f.fieldName,
                                ZoneNumber: f.zoneId ? f.zoneNumber : null
                            };
                        })).then(r => this.svc.refresh()
                            .then(r => this.cancel()));
                }
            });

    }

}
